import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GraduationCap, Code, Calculator, BookOpen, Globe, Link2, Lock,
  Search, Folder, ChevronRight, Compass, Plus
} from "lucide-react";
import { useAuthContext } from "../../hooks/useAuthContext";

const defaultCategoryIcons = {
  GraduationCap,
  Code,
  Calculator,
  BookOpen,
  Folder,
};

const defaultCategoryColors = [
  "text-primary",
  "text-muted-foreground/60",
  "text-amber-500",
  "text-chart-3",
  "text-emerald-500",
  "text-blue-500",
];

const visibilityIcon = {
  public: Globe,
  unlisted: Link2,
  private: Lock,
};

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'created', label: 'Created' },
  { id: 'joined', label: 'Joined' },
];

const LibraryRightSidebar = ({ onOpenDiscover }) => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const { token } = useAuthContext();
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/folders?type=${tab}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setFolders(await res.json());
    } catch (err) {
      console.error("Error fetching folders:", err);
    } finally {
      setLoading(false);
    }
  }, [token, tab]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const visible = folders.filter((f) =>
    !query.trim() || (f.name || '').toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your folders..."
            className="w-full h-9 pl-9 pr-3 rounded-xl bg-muted border-0 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <button
          onClick={() => navigate(`/${lang}/add-category`)}
          title="New folder"
          className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center border-none cursor-pointer hover:bg-primary/90 transition-colors flex-shrink-0"
        >
          <Plus className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>

      <div className="flex items-center gap-1 p-1 bg-muted/40 rounded-xl">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 h-8 rounded-lg text-[11px] font-bold transition-colors border-none cursor-pointer ${
              tab === t.id ? 'bg-background text-foreground shadow-sm' : 'bg-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-primary" />
          </div>
        ) : visible.length > 0 ? (
          visible.map((folder, i) => {
            const VisIcon = visibilityIcon[folder.visibility || 'private'] || Lock;
            const Icon = defaultCategoryIcons[folder.icon] || Folder;
            const colorClass = defaultCategoryColors[i % defaultCategoryColors.length];

            return (
              <div
                key={folder.id}
                onClick={() => navigate(`/${lang}/categories?id=${folder.id}`)}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-all group cursor-pointer border border-transparent hover:border-border/50"
              >
                <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-muted group-hover:bg-card shadow-sm transition-all ${colorClass}`}>
                  <Icon size={18} strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-foreground group-hover:text-primary transition-colors truncate">{folder.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {folder.source_folder_id ? 'Joined collection' : (folder.description || 'Your collection')}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[10px] font-bold text-foreground">{folder.pdf_ids?.length || 0}</span>
                  <VisIcon className="w-2.5 h-2.5 text-muted-foreground" />
                </div>
              </div>
            );
          })
        ) : (
          <div className="py-10 text-center border border-dashed border-border/50 rounded-xl bg-muted/20">
            <p className="text-[11px] text-muted-foreground font-medium">
              {query.trim() ? 'No folder matches that search.' : tab === 'joined' ? "You haven't joined any collections yet." : 'No folders yet.'}
            </p>
          </div>
        )}
      </div>

      <button
        onClick={onOpenDiscover}
        className="w-full flex items-center justify-between gap-3 p-4 rounded-2xl bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors cursor-pointer text-left"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Compass className="w-4 h-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-xs font-bold text-foreground">Discover collections</span>
            <span className="block text-[10px] text-muted-foreground">Find folders other students share</span>
          </span>
        </span>
        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      </button>
    </div>
  );
};

export default LibraryRightSidebar;
